import { useState } from 'react';
import {
  LayoutDashboard,
  FileText,
  Users,
  UserPlus,
  IdCard,
  UserCog,
  AlertTriangle,
  Wallet,
  Bell,
  FileSearch,
  BarChart3,
  FolderOpen,
  LogOut,
  MessageSquare,
  Clock,
  Settings,
  Monitor,
  ChevronDown,
  List,
  GitBranch,
  Receipt,
  Sliders,
  LucideIcon
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRealTimeState } from './main/reducer/RealTimeContext';
import { useUserDispatch } from '../context';
import { handleLogout } from '../utils/logout';
import { toast } from 'sonner';
import { useNewIndicator } from '@/hooks/useNewIndicator';
import {
  getSidebarBadgeText,
  hasSidebarBadge,
} from './sidebarBadgeUtils.mjs';

interface MenuItem {
  icon: LucideIcon;
  label: string;
  path?: string;
  badge?: number | string;
  children?: MenuItem[];
}

interface MenuSection {
  title: string;
  items: MenuItem[];
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useUserDispatch();
  const { registerUserData, requestBankingData } = useRealTimeState();
  const abuseIndicator = useNewIndicator("abuse");
  const profitIndicator = useNewIndicator("profitAlert");

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev =>
      prev.includes(label) ? prev.filter(l => l !== label) : [...prev, label]
    );
  };

  const onToggle = () => setIsCollapsed(prev => !prev);

  const onLogout = async () => {
    setLogoutLoading(true);
    try {
      await handleLogout(navigate, dispatch);
      toast.success('로그아웃되었습니다');
    } catch (error) {
      toast.error('로그아웃 중 오류가 발생했습니다');
    } finally {
      setLogoutLoading(false);
    }
  };

  // Context에서 실시간 카운트 가져오기
  const newRegisterCount = registerUserData?.length ?? 0;
  const newBankingRequestCount = requestBankingData?.length ?? 0;
  const { customerInquiryData } = useRealTimeState() as any;
  const newInquiryCount = customerInquiryData?.length ?? 0;
  const checkAbuseBadge =
    abuseIndicator.hasNew || profitIndicator.hasNew ? "NEW" : undefined;
  
  // Helper to check if item is active
  const isActive = (path: string) => {
    const currentPath = location.pathname.replace('/dashboard/', '');
    return currentPath === path;
  };

  const menuSections: MenuSection[] = [
    {
      title: '회원관리',
      items: [
        { icon: LayoutDashboard, label: '현황판', path: 'main' },
        { icon: FileText, label: '주문별 현황판', path: 'orderStatusByItem' },
        { icon: Users, label: '회원별 주문현황', path: 'orderStatus' },
        { icon: UserPlus, label: '회원목록', path: 'memberList' },
        { icon: IdCard, label: '회원가입신청현황', path: 'registerStatus', badge: newRegisterCount },
        { icon: AlertTriangle, label: '이상거래 감지', path: 'checkAbuse', badge: checkAbuseBadge },
      ],
    },
    {
      title: '추천인관리',
      items: [
        { icon: List, label: '추천인 목록', path: 'manageRecommender' },
        { icon: GitBranch, label: '계층 구조관리', path: 'recommenderHierarchy' },
        { icon: Receipt, label: '수수료 프리셋', path: 'recommenderFeePreset' },
        { icon: Sliders, label: '정산 설정', path: 'recommenderSettlementConfig' },
        { icon: FolderOpen, label: '추천인별 손익내역', path: 'recommendedProfitNLoss' },
      ],
    },
    {
      title: '입출금관리',
      items: [
        { icon: Wallet, label: '입출금신청현황', path: 'depositNWithdrawalStatus', badge: newBankingRequestCount },
        { icon: Bell, label: '입금 공지사항', path: 'depositNotice' },
        { icon: FileSearch, label: '입출금내역조회', path: 'depositNWithdrawalInquiry' },
        { icon: BarChart3, label: '입출금통계', path: 'depositNWithdrawal' },
      ],
    },
    {
      title: '내역조회',
      items: [
        { icon: FolderOpen, label: '전체손익내역', path: 'totalProfitNLoss' },
        { icon: FolderOpen, label: '회원 총손익 집계내역', path: 'memberTotalProfitNLoss' },
        { icon: FolderOpen, label: '회원 일별손익내역', path: 'memberDailyProfitNLoss' },
        { icon: FolderOpen, label: '주문체결내역', path: 'orderSignedHistory' },
        { icon: FolderOpen, label: '클라이언트 로그', path: 'memberOrderHistory' },
        { icon: FolderOpen, label: '회원담보금보정내역', path: 'MemberAccountBalanceReviseHistory' },
      ],
    },
    {
      title: '이력조회',
      items: [
        { icon: Monitor, label: '실시간 접속내역', path: 'liveTimeLoginHistory' },
        { icon: FolderOpen, label: '회원접속이력 조회', path: 'loginHisotryInquiry' },
        { icon: Bell, label: '공지사항', path: 'notice' },
        { icon: MessageSquare, label: '1:1 문의관리', path: 'customerInquiry', badge: newInquiryCount },
        { icon: FolderOpen, label: '관리자 접속 이력조회', path: 'managerLoginHistory' },
        { icon: FolderOpen, label: '관리자 작업 이력조회', path: 'managerWorkHistory' },
      ],
    },
    {
      title: '계좌관련',
      items: [
        { icon: Wallet, label: '입금계좌관리', path: 'manageDepositAccount' },
        { icon: Settings, label: '거래종목관리(국내)', path: 'manageDomestic' },
        { icon: Settings, label: '거래종목관리(해외)', path: 'manageForeign' },
        { icon: Clock, label: '거래시간관리(국내)', path: 'manageDomesticTradingTime' },
        { icon: Clock, label: '거래시간관리(해외)', path: 'manageForeignTradingTime' },
        { icon: Settings, label: '비밀번호변경', path: 'changePassword' },
      ],
    },
  ];

  return (
    <aside
      className={`bg-white shadow-lg transition-all duration-300 flex flex-col h-full overflow-hidden ${
        isCollapsed ? 'w-20' : 'w-64'
      }`}
    >
      {/* Header with HTS MANAGER and Logout */}
      <div className="h-14 px-4 flex items-center justify-between border-b border-gray-200">
        {!isCollapsed ? (
          <>
            <h1 className="text-lg font-semibold text-gray-900">HTS MANAGER</h1>
            <button
              onClick={onLogout}
              className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="로그아웃"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </>
        ) : (
          <button
            onClick={onLogout}
            className="p-2 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors mx-auto"
            title="로그아웃"
          >
            <LogOut className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-4">
        {menuSections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-6">
            {!isCollapsed && (
              <h3 className="px-4 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                {section.title}
              </h3>
            )}
            
            <nav className="space-y-1 px-3">
              {section.items.map((item, itemIndex) => {
                const Icon = item.icon;

                // Dropdown 하위 메뉴가 있는 항목
                if (item.children) {
                  const hasActiveChild = item.children.some(child => child.path && isActive(child.path));
                  const isMenuExpanded = expandedMenus.includes(item.label) || hasActiveChild;

                  return (
                    <div key={itemIndex}>
                      <button
                        onClick={() => toggleMenu(item.label)}
                        className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all group ${
                          hasActiveChild
                            ? 'bg-blue-50 text-blue-700 font-medium shadow-sm'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                        title={isCollapsed ? item.label : undefined}
                      >
                        <Icon
                          className={`w-5 h-5 flex-shrink-0 ${
                            hasActiveChild ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                          }`}
                        />
                        {!isCollapsed && (
                          <div className="flex-1 text-left flex justify-between items-center">
                            <span className="text-sm truncate">{item.label}</span>
                            <ChevronDown
                              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                                isMenuExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          </div>
                        )}
                      </button>
                      {isMenuExpanded && !isCollapsed && (
                        <div className="mt-1 ml-3 space-y-0.5 border-l-2 border-gray-200 pl-3">
                          {item.children.map((child, childIndex) => {
                            const ChildIcon = child.icon;
                            const childActive = child.path ? isActive(child.path) : false;
                            return (
                              <button
                                key={childIndex}
                                onClick={() => child.path && navigate(`/dashboard/${child.path}`)}
                                className={`w-full flex items-center space-x-2.5 px-2.5 py-2 rounded-lg transition-all group ${
                                  childActive
                                    ? 'bg-blue-50 text-blue-700 font-medium'
                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                              >
                                <ChildIcon
                                  className={`w-4 h-4 flex-shrink-0 ${
                                    childActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                                  }`}
                                />
                                <span className="text-[13px] truncate">{child.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }

                // 일반 메뉴 항목
                const active = item.path ? isActive(item.path) : false;
                const showBadge = hasSidebarBadge(item.badge);
                const badgeText = getSidebarBadgeText(item.badge);

                return (
                  <button
                    key={itemIndex}
                    onClick={() => item.path && navigate(`/dashboard/${item.path}`)}
                    className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all group ${
                      active
                        ? 'bg-blue-50 text-blue-700 font-medium shadow-sm'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <div className="relative">
                        <Icon
                        className={`w-5 h-5 flex-shrink-0 ${
                            active ? 'text-blue-600' : 'text-gray-500 group-hover:text-gray-700'
                        }`}
                        />
                        {isCollapsed && showBadge && (
                            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                            </span>
                        )}
                    </div>

                    {!isCollapsed && (
                      <div className="flex-1 text-left flex justify-between items-center">
                        <span className="text-sm truncate">{item.label}</span>
                         {showBadge && (
                             <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                                 {badgeText}
                             </span>
                         )}
                      </div>
                    )}
                  </button>
                );
              })}
            </nav>
            
            {!isCollapsed && sectionIndex < menuSections.length - 1 && (
              <div className="mx-3 mt-4 border-b border-gray-200" />
            )}
          </div>
        ))}
      </div>
          
    </aside>
  );
}
